import { MessageSource } from './enums'
import { MessageSourceRegistry } from './MessageSourceRegistry'
import { ButtonMessageHandler } from './handlers/ButtonMessageHandler'
import { ChatMessageHandler } from './handlers/ChatMessageHandler'
import { GreetingMessageHandler } from './handlers/GreetingMessageHandler'
import { isValidMessageData } from './type-guards'

export class OptimizedMessageListener {
  private readonly registry: MessageSourceRegistry
  private readonly buttonHandler: ButtonMessageHandler
  private readonly chatHandler: ChatMessageHandler
  private readonly greetingHandler: GreetingMessageHandler
  private handler: ((event: MessageEvent) => void) | null = null
  private isAttached: boolean = false

  constructor(
    registry: MessageSourceRegistry,
    buttonHandler: ButtonMessageHandler,
    chatHandler: ChatMessageHandler,
    greetingHandler: GreetingMessageHandler
  ) {
    this.registry = registry
    this.buttonHandler = buttonHandler
    this.chatHandler = chatHandler
    this.greetingHandler = greetingHandler
    this.createHandler()
  }

  private createHandler(): void {
    this.handler = (event: MessageEvent) => {
      try {
        if (
          event.data &&
          typeof event.data === 'object' &&
          ('source' in event.data || '__REACT_DEVTOOLS' in event.data)
        ) {
          return
        }
        
        if (!isValidMessageData(event.data)) {
          return
        }

        const source = this.registry.getSourceType(event)
        if (!source) {
          return
        }

        const { type, payload } = event.data

        if (!type || typeof type !== 'string') {
          return
        }

        try {
          switch (source) {
            case MessageSource.BUTTON:
              this.buttonHandler.handle(event, type, payload)
              break
            case MessageSource.GREETING:
              this.greetingHandler.handle(event, type, payload)
              break
            case MessageSource.CHAT:
              this.chatHandler.handle(event, type, payload)
              break
          }
        } catch (handlerError) {
          console.error(`[OptimizedMessageListener] Error in ${source} handler:`, handlerError)
        }
      } catch (error) {
        console.error('[OptimizedMessageListener] Unexpected error processing message:', error)
      }
    }
  }

  attach(): void {
    if (this.handler && !this.isAttached) {
      try {
        window.addEventListener('message', this.handler)
        this.isAttached = true
      } catch (error) {
        console.error('[OptimizedMessageListener] Error attaching listener:', error)
      }
    }
  }

  detach(): void {
    if (this.handler && this.isAttached) {
      try {
        window.removeEventListener('message', this.handler)
        this.isAttached = false
      } catch (error) {
        console.error('[OptimizedMessageListener] Error detaching listener:', error)
      }
    }
  }
}
